import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta, ldScripts, breadcrumbLd, faqLd, SITE_ORIGIN } from "@/lib/seo";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { ButtonLink } from "@/components/ui-ext/Button";
import { ResourceLibrary } from "@/components/resources/ResourceLibrary";
import { AuditVisualizer } from "@/components/resources/AuditVisualizer";
import { SchemaGraphExplorer } from "@/components/resources/SchemaGraphExplorer";
import { RESOURCES } from "@/content/resources";


export const Route = createFileRoute("/resources")({
  head: () => {
    const path = "/resources/";
    const base = pageMeta({
      path,
      title: "SEO Resource Center — Austin SEO Guides, Field Notes, Tools & Glossary | Optimize Plus",
      description:
        "The Optimize Plus resource centre from Austin, Texas: long-form SEO guides, editorial field notes, free interactive tools, and a practitioner glossary — a working reference for founders and marketing leads, not a lead magnet.",
      keywords: [
        "SEO resources",
        "Austin SEO learning hub",
        "SEO guides free",
        "AI search resources",
        "GEO learning",
        "SEO knowledge base",
        "SEO glossary",
      ],
    });
    const url = `${SITE_ORIGIN}${path}`;
    const collection = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Optimize Plus Resource Center",
      url,
      about: ["SEO", "AEO", "GEO", "Local SEO", "Technical SEO"],
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", "@id": `${SITE_ORIGIN}/#website` },
      mainEntity: {
        "@type": "ItemList",
        name: "Free SEO templates and checklists",
        itemListElement: RESOURCES.map((r, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: r.title,
          description: r.summary,
          url: `${url}#${r.slug}`,
        })),
      },
    };
    return {
      ...base,
      scripts: ldScripts(
        collection,
        breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Resources", path },
        ]),
        faqLd([
          {
            q: "Are the Optimize Plus templates and checklists really free?",
            a: "Yes. Every template, checklist, and tool in the resource centre downloads directly in your browser. There is no form, no email capture, and no sign-up.",
          },
          {
            q: "What formats do the downloads come in?",
            a: "Checklists and scorecards download as CSV so you can drop them straight into a spreadsheet. Plans, runbooks, and briefs download as Markdown so they paste cleanly into Notion, Docs, or a repo.",
          },
          {
            q: "Can I use these resources with my own team or clients?",
            a: "Yes. They are the working documents we use inside Optimize Plus engagements, and you are welcome to adapt them for internal or client use.",
          },
          {
            q: "How is the SEO readiness score calculated?",
            a: "The interactive audit weights five pillars — crawl and index (22%), render and speed (20%), structure and schema (18%), content and intent (22%), and authority and local (18%) — then scores each pillar by the share of its checks you have in place.",
          },
          {
            q: "How is the resource library organised?",
            a: "Into four layers that build on each other: long-form pillar guides for strategy and planning, editorial field notes for what's currently changing, interactive tools for hands-on diagnostics, and a glossary for precise definitions — each layer links to the others where relevant.",
          },
          {
            q: "Where should a founder or non-specialist start?",
            a: "Start with the SEO readiness audit visualiser to see where your own site stands, then read the pillar guide covering your weakest scoring area, referring to the glossary whenever a term is unfamiliar.",
          },
          {
            q: "How often is the resource centre updated?",
            a: "We review the guides and glossary quarterly against algorithm changes, new AI-search behaviour, and what we're seeing across live engagements, and update or retire content that no longer reflects current practice.",
          },
        ]),
      ),
    };

  },
  component: ResourcesHub,
});

const SECTIONS = [
  {
    to: "/guides/" as const,
    eyebrow: "Pillar guides",
    title: "Long-form guides",
    body: "End-to-end operating manuals. Each guide is a working plan, not a summary — designed to be used inside a program, not just read.",
    cta: "Browse guides",
    items: ["The Complete Guide to SEO in 2026", "The AEO / GEO Playbook", "Local SEO Playbook", "Technical SEO Audit Checklist"],
  },
  {
    to: "/blog/" as const,
    eyebrow: "Field notes",
    title: "The publication",
    body: "Editorial notes from live engagements — what changed in search this month, what earned citations, what stopped working.",
    cta: "Read the publication",
    items: ["AI Overviews in 2026", "AI Search Readiness Scorecard explained", "Topical authority as a network property", "The analytics stack we actually use"],
  },
  {
    to: "/tools/" as const,
    eyebrow: "Interactive tools",
    title: "Free tools",
    body: "Small, sharp tools we built for ourselves and opened up. No sign-up. No email. They run in the browser.",
    cta: "Open the toolbox",
    items: ["SERP Preview Simulator", "Schema Explorer", "Meta Length Checker", "GEO Matrix Scorer (beta)"],
  },
  {
    to: "/glossary/" as const,
    eyebrow: "Reference",
    title: "SEO Glossary",
    body: "36 terms across classical SEO, AI search, local, technical, and analytics — defined the way we use them in engagements.",
    cta: "Open the glossary",
    items: ["AEO", "GEO", "Core Web Vitals", "Topical authority", "E-E-A-T", "and 31 more"],
  },
] as const;

function ResourcesHub() {
  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <section className="container-page pt-24 pb-16">
          <EyebrowLabel>Resources</EyebrowLabel>
          <h1 className="mt-4 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
            A working library for
            <br />
            <span className="text-primary">modern search.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">
            Guides you can plan against. Field notes from live work. Tools that run in the browser.
            Definitions we actually use. Written from Austin, Texas — everything here is the same
            thinking we bring to engagements with Austin and Central Texas operators.
          </p>
        </section>

        <section className="container-page pb-24 grid gap-8 md:grid-cols-2">
          {SECTIONS.map((s) => (
            <Link
              key={s.to}
              to={s.to  }
              className="group rounded-3xl border border-border p-8 md:p-10 bg-card/40 hover:bg-card/60 hover:border-primary/40 transition-all"
            >
              <EyebrowLabel>{s.eyebrow}</EyebrowLabel>
              <h2 className="mt-3 font-display text-3xl md:text-4xl tracking-tight">{s.title}</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">{s.body}</p>
              <ul className="mt-6 space-y-1.5 text-sm text-foreground/85">
                {s.items.map((i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-primary">·</span> {i}
                  </li>
                ))}
              </ul>
              <span className="mt-8 inline-flex items-center gap-2 text-sm text-primary group-hover:gap-3 transition-all">
                {s.cta} <span aria-hidden>→</span>
              </span>
            </Link>
          ))}
        </section>

        <section id="downloads" className="container-page pb-24 scroll-mt-24">
          <EyebrowLabel>Templates & checklists</EyebrowLabel>
          <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-tight max-w-3xl">
            The documents we actually run engagements with.
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">
            Not lead magnets. These are the working checklists, runbooks, and reporting
            skeletons we use internally at Optimize Plus. Download them as CSV or Markdown —
            no form, no email, generated in your browser.
          </p>
          <div className="mt-10">
            <ResourceLibrary />
          </div>
        </section>

        <section id="audit-visualizer" className="container-page pb-24 scroll-mt-24">
          <EyebrowLabel>Interactive · SEO audit visualiser</EyebrowLabel>
          <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-tight max-w-3xl">
            Score your own foundation in two minutes.
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">
            Tick what's genuinely true on your site. The score is weighted the way we weight
            it in a real audit — crawl and content carry more than schema — and the panel
            ranks what to fix next by impact, not by how easy it is.
          </p>
          <div className="mt-10">
            <AuditVisualizer />
          </div>
        </section>

        <section id="schema-explorer" className="container-page pb-24 scroll-mt-24">
          <EyebrowLabel>Interactive · Schema graph explorer</EyebrowLabel>
          <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-tight max-w-3xl">
            How the JSON-LD nodes reference each other.
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">
            Most schema tutorials show isolated blocks. The part that matters is the wiring:
            stable <code className="font-mono text-sm text-primary">@id</code> values that let
            Organization, LocalBusiness, WebSite, Article, and Person resolve to one entity
            instead of eight strangers. Pick a node to see what it does and copy the snippet.
          </p>
          <div className="mt-10">
            <SchemaGraphExplorer />
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            Want to generate your own?{" "}
            <Link to="/tools/$slug/" params={{ slug: "local-business-schema" }} className="text-primary underline underline-offset-4">
              LocalBusiness Schema Generator
            </Link>{" "}
            and the{" "}
            <Link to="/tools/$slug/" params={{ slug: "schema-explorer" }} className="text-primary underline underline-offset-4">
              Schema Explorer tool
            </Link>{" "}
            do the boilerplate for you.
          </p>
        </section>



        <section className="container-page pb-32">
          <div className="rounded-3xl border border-border p-10 md:p-14 bg-gradient-to-br from-primary/10 to-transparent">
            <EyebrowLabel>Working with us</EyebrowLabel>
            <h2 className="mt-3 font-display text-3xl md:text-5xl tracking-tight max-w-3xl">
              These resources are the same thinking we bring to engagements.
            </h2>
            <p className="mt-5 max-w-2xl text-muted-foreground leading-relaxed">
              If any of it maps to a problem you're carrying, the fastest next step is a 30-minute
              conversation. We'll tell you what we'd do, whether we're the right fit, and either
              way you'll leave with a clearer picture.
            </p>
            <div className="mt-8">
              <ButtonLink to={"/contact/" as any} params={{} as any} search={{ location: "", service: "resources" } as any} size="lg">Start a project <span aria-hidden>→</span></ButtonLink>
            </div>
          </div>
        </section>

        <section className="container-page pb-24 max-w-3xl">
          <EyebrowLabel>How this library is organised</EyebrowLabel>
          <h2 className="mt-3 font-display text-3xl md:text-4xl tracking-tight">
            Four layers, built to be used together.
          </h2>
          <div className="mt-6 space-y-5 text-sm leading-relaxed text-foreground/85">
            <p>
              Most agency "resource centres" are a pile of blog posts sorted by publish date, which is easy to build but useless to navigate. We organised ours around how the work actually gets done: strategy first, then current context, then hands-on diagnosis, then precise language. Each layer exists because the one before it creates a need the next one answers.
            </p>
            <p>
              Pillar guides sit at the top because they are the operating plan — what to do, in what order, and why. They are deliberately long, because a genuine operating manual for technical SEO, local SEO, or AEO/GEO cannot be compressed into a listicle without losing the sequencing that makes it work. Field notes sit underneath because search changes month to month, and a static guide cannot capture what's shifting right now in AI Overviews, algorithm updates, or what's earning citations this quarter — the publication is where that lives. Interactive tools exist because reading about a problem and diagnosing your own site are different tasks; the audit visualiser, schema explorer, and SERP simulator let you apply the thinking to your own domain rather than take our word for it. The glossary sits at the base because none of the above works if the vocabulary is unclear — every guide and field note links back to relevant terms rather than re-explaining them inline.
            </p>
          </div>

          <h3 className="mt-10 font-display text-2xl">Recommended paths by role</h3>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <div className="surface-card p-5">
              <div className="text-primary text-xs uppercase tracking-widest">Founder / owner-operator</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">Run the audit visualiser first to see where you stand, read the Local SEO Playbook if you serve a physical area, then skim the glossary entries for E-E-A-T and Core Web Vitals so vendor conversations make sense.</p>
            </div>
            <div className="surface-card p-5">
              <div className="text-primary text-xs uppercase tracking-widest">In-house marketing lead</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">Start with the Complete Guide to SEO for the full operating structure, download the reporting-skeleton template to check your own tracking setup, then follow the publication monthly to stay current on AI-search shifts.</p>
            </div>
            <div className="surface-card p-5">
              <div className="text-primary text-xs uppercase tracking-widest">Technical / developer</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">Go straight to the Technical SEO Audit Checklist and the Schema Explorer, use the Schema Generator tool for implementation, and reference the glossary for precise definitions of crawl budget, rendering, and structured data terms.</p>
            </div>
          </div>

          <h3 className="mt-10 font-display text-2xl">A sensible sequence through guides, tools, and glossary</h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            If you are working through the library systematically rather than dipping in for one answer, this order tends to build understanding rather than fragment it: read the relevant pillar guide in full before touching a tool, since the guide explains why a metric matters and the tool only shows you the number. Run the audit visualiser or relevant diagnostic tool second, so the guide's abstractions become specific findings about your own site. Use the glossary as a reference throughout rather than reading it front to back — it is built for lookup, not linear reading. Finally, check the publication for anything that might have changed since the guide was last reviewed; search moves fast enough that a guide written eight months ago can be missing a recent shift in how AI Overviews cite sources.
          </p>

          <h3 className="mt-10 font-display text-2xl">What we update on a quarterly cycle</h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            Every quarter we review the pillar guides against what actually happened in search over the previous three months — confirmed algorithm updates, changes to how AI Overviews or ChatGPT cite sources, and shifts we've observed directly across live engagements. Guides get updated in place rather than replaced with a new post, so a bookmarked link stays current. The glossary gets the same review: terms get sharpened as our own usage settles, and new terms get added as the field introduces them — GEO and AEO, for instance, were added once they became terms clients were actually asking about, not before. Interactive tools are checked for accuracy against current best practice — for example, the audit visualiser's pillar weightings are revisited if the balance between technical health and content quality shifts in how search engines actually reward sites. Anything that no longer reflects how we'd advise a client today gets flagged and either rewritten or retired, rather than left live to quietly go stale.
          </p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
